import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { apiClient } from '../../services/api';
import toast from 'react-hot-toast';

interface WorkModeToggleProps {
    initialModes?: string[];
    userId?: string;
    onUpdate?: (modes: string[]) => void;
    readOnly?: boolean;
}

const MODES = [
    { id: 'employment', label: 'Open to Employment', icon: '🏢', color: 'blue' },
    { id: 'freelance', label: 'Open to Freelance', icon: '💻', color: 'green' },
    { id: 'local-gig', label: 'Open to Local Gigs', icon: '🛠️', color: 'orange' },
    { id: 'remote', label: 'Open to Remote Global', icon: '🌍', color: 'purple' },
    { id: 'apprenticeship', label: 'Open to Apprenticeship', icon: '🎓', color: 'yellow' },
    { id: 'investment', label: 'Open to Investment', icon: '📈', color: 'gold' }
];

const WorkModeToggle: React.FC<WorkModeToggleProps> = ({ initialModes = [], onUpdate, readOnly = false }) => {
    const [activeModes, setActiveModes] = useState<string[]>(initialModes);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setActiveModes(initialModes);
    }, [initialModes]);

    const toggleMode = async (modeId: string) => {
        if (readOnly || loading) return;

        const newModes = activeModes.includes(modeId)
            ? activeModes.filter(m => m !== modeId)
            : [...activeModes, modeId];

        setActiveModes(newModes);
        setLoading(true);

        try {
            await apiClient.updateWorkModes({ workModes: newModes });
            toast.success('Work modes updated');
            if (onUpdate) onUpdate(newModes);
        } catch (err) {
            console.error('Failed to update work modes:', err);
            toast.error('Failed to update work modes');
            // Revert on failure
            setActiveModes(activeModes);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {MODES.map((mode) => {
                const isActive = activeModes.includes(mode.id);

                return (
                    <motion.button
                        key={mode.id}
                        whileHover={!readOnly ? { scale: 1.02, translateY: -2 } : {}}
                        whileTap={!readOnly ? { scale: 0.98 } : {}}
                        onClick={() => toggleMode(mode.id)}
                        disabled={readOnly || loading}
                        className={`flex items-center gap-3 p-4 rounded-xl border transition-all text-left relative overflow-hidden ${isActive
                                ? 'bg-premium-gold/10 border-premium-gold/40 text-premium-gold shadow-[0_0_15px_rgba(212,175,55,0.1)]'
                                : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20 hover:bg-white/10'
                            } ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
                    >
                        <span className="text-2xl">{mode.icon}</span>
                        <div className="flex-1">
                            <div className="font-bold text-sm">{mode.label}</div>
                            {isActive && (
                                <motion.div
                                    initial={{ opacity: 0, x: -5 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="text-[10px] uppercase tracking-wider font-bold mt-1 opacity-70"
                                >
                                    Active
                                </motion.div>
                            )}
                        </div>

                        {isActive && (
                            <motion.div
                                layoutId="active-indicator"
                                className="absolute top-2 right-2 w-2 h-2 rounded-full bg-premium-gold shadow-[0_0_8px_#d4af37]"
                            />
                        )}
                    </motion.button>
                );
            })}
        </div>
    );
};

export default WorkModeToggle;
